import React, { useMemo } from "react";
import theme from "../../../styles/theme";
import TsProperties from "../technical_program/TsProperties";
import {
  getDeleteProtectionState,
  updateWorkbenchItem,
  updateWorkbenchItemField,
} from "./AssetWorkbench";
import AssetSpatialAssignmentPanel from "./AssetSpatialAssignmentPanel";

/* ============================================================================
   STYLE BASE DES SELECTS
============================================================================ */
const selectBaseStyle = {
  width: "100%",
  height: 28,
  minHeight: 28,
  padding: "2px 8px",
  boxSizing: "border-box",
  fontFamily: theme.form.field.fontFamily,
  fontSize: theme.form.field.fontSize,
  fontWeight: theme.form.field.fontWeight,
  color: theme.form.field.color,
  borderRadius: theme.borders.radius.md,
  border: `1px solid ${theme.colors.borders.default}`,
  background: theme.colors.background.input,
  outline: "none",
  transition: theme.form.field.transition,
};

/* ============================================================================
   STYLE COMMUN HEADER
============================================================================ */
const panelHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing.md,
  paddingBottom: theme.spacing.xs,
  marginBottom: theme.spacing.sm,
};

const detailGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing.sm,
};

/* ============================================================================
   PANEL GÉNÉRIQUE — DÉTAILS
============================================================================ */
const AssetWorkbenchDetailsPanel = ({
  title = "",
  subtitle = "",
  items = [],
  selectedItemId = null,
  familyMeta = {},
  fieldSchema = {},
  SymbolComponent,
  onItemsChange,
  onDuplicateItem,
  onDeleteItem,
  readOnlyMode = false,
  disabled = false,
  emptyMessage = "Aucun élément sélectionné.",
  style = {},
  spatialEntities = [],
  onAddSpatialAssignment,
  onRemoveSpatialAssignment,
  showSpatialAssignment = false,
  spatialPanelOpen = false,
  onSpatialPanelToggle,
}) => {
  const selectedItem = items.find((item) => item.id === selectedItemId) ?? null;

  const meta = selectedItem
    ? familyMeta[selectedItem.family] || buildFallbackMeta(selectedItem.family)
    : null;

  const deleteState = useMemo(() => {
    if (!selectedItem) {
      return {
        canDelete: false,
        reason: "",
      };
    }

    return getDeleteProtectionState(items, selectedItem.id);
  }, [items, selectedItem]);

  const isSpatialOpen = showSpatialAssignment && spatialPanelOpen;

  const toggleActive = (itemId) => {
    if (readOnlyMode || disabled || !onItemsChange) return;

    const nextItems = updateWorkbenchItem(items, itemId, (item) => ({
      ...item,
      active: !item.active,
    }));

    onItemsChange(nextItems);
  };

  const updateField = (itemId, fieldName, value) => {
    if (readOnlyMode || disabled || !onItemsChange) return;

    const nextItems = updateWorkbenchItemField({
      items,
      itemId,
      fieldName,
      value,
      fieldSchema,
    });

    onItemsChange(nextItems);
  };

  const handleSpatialToggle = () => {
    if (!showSpatialAssignment || disabled || typeof onSpatialPanelToggle !== "function") {
      return;
    }
    onSpatialPanelToggle(!spatialPanelOpen);
  };

  if (!selectedItem) {
    return (
      <div
        style={{
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.sm,
          ...style,
        }}
      >
        <div style={panelHeaderStyle}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div
              style={{
                fontFamily: theme.fonts.heading,
                fontSize: theme.fontSizes.xl,
                fontWeight: theme.fontWeights.semiBold,
                color: theme.colors.text.primary,
                lineHeight: 1.15,
              }}
            >
              {title}
            </div>

            <div
              style={{
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.sm,
                fontWeight: theme.fontWeights.normal,
                color: theme.colors.text.small,
                lineHeight: 1.35,
              }}
            >
              {subtitle}
            </div>
          </div>
        </div>

        <div
          style={{
            border: `1px dashed ${theme.colors.borders.default}`,
            borderRadius: theme.borders.radius.xl,
            padding: theme.spacing.lg,
            background: theme.colors.background.Card,
            color: theme.colors.text.small,
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.sm,
            lineHeight: 1.4,
          }}
        >
          {emptyMessage}
        </div>
      </div>
    );
  }

  const familyConfig = fieldSchema[selectedItem.family] || {};
  const quickFields = familyConfig.quickFields || [];
  const detailFields = familyConfig.detailFields || [];

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing.sm,
        ...style,
      }}
    >
      <div style={panelHeaderStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontFamily: theme.fonts.heading,
              fontSize: theme.fontSizes.xl,
              fontWeight: theme.fontWeights.semiBold,
              color: theme.colors.text.primary,
              lineHeight: 1.15,
            }}
          >
            {title}
          </div>

          <div
            style={{
              fontFamily: theme.fonts.body,
              fontSize: theme.fontSizes.sm,
              fontWeight: theme.fontWeights.normal,
              color: theme.colors.text.small,
              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------
         Layout interne adaptatif
      ------------------------------------------------------------------ */}
      <div
        style={{
          background: "transparent",
          border: "none",
          borderRadius: 0,
          boxShadow: "none",
          padding: theme.spacing.xs,
          display: "grid",
          gridTemplateColumns: isSpatialOpen
            ? "minmax(0, 1.15fr) minmax(280px, 0.85fr)"
            : "1fr",
          gap: theme.spacing.md,
          alignItems: "start",
          transition: "all 0.16s ease",
        }}
      >
        {/* ==============================================================
           COLONNE GAUCHE — METIER
        ============================================================== */}
        <div
          style={{
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing.sm,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: theme.spacing.md,
            }}
          >
            <div style={{ display: "flex", gap: theme.spacing.sm, minWidth: 0 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  minWidth: 44,
                  borderRadius: theme.borders.radius.lg,
                  background: meta.lightBg,
                  border: `1px solid ${meta.lightBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {SymbolComponent ? (
                  <SymbolComponent family={selectedItem.family} color={meta.color} />
                ) : null}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontFamily: theme.fonts.heading,
                    fontSize: theme.fontSizes.md,
                    fontWeight: theme.fontWeights.semiBold,
                    color: theme.colors.text.primary,
                    lineHeight: 1.2,
                  }}
                >
                  {selectedItem.title}
                </div>

                <div
                  style={{
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fontSizes.sm,
                    color: theme.colors.text.small,
                    lineHeight: 1.25,
                  }}
                >
                  {selectedItem.subtitle}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => toggleActive(selectedItem.id)}
              disabled={readOnlyMode || disabled}
              style={{
                height: 28,
                padding: "0 10px",
                borderRadius: theme.borders.radius.full,
                border: `1px solid ${
                  selectedItem.active ? meta.color : theme.colors.borders.default
                }`,
                background: selectedItem.active
                  ? meta.lightBg
                  : theme.colors.background.input,
                color: selectedItem.active ? meta.color : theme.colors.text.small,
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.xs,
                fontWeight: theme.fontWeights.medium,
                cursor: readOnlyMode || disabled ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {selectedItem.active ? "Actif" : "Inactif"}
            </button>
          </div>

{showSpatialAssignment && (
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      width: "100%",
    }}
  >
    <button
      type="button"
      onClick={handleSpatialToggle}
      disabled={disabled}
      style={{
        height: 32,
        width: "fit-content",
        padding: "0 2px",
        border: "none",
        background: "transparent",
        color: theme.colors.text.caneva,
        fontFamily: theme.fonts.body,
        fontSize: theme.fontSizes.xs,
        fontWeight: spatialPanelOpen
          ? theme.fontWeights.semiBold
          : theme.fontWeights.medium,
        cursor: disabled ? "default" : "pointer",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        transition: "all 0.14s ease",
      }}
    >
      <span>{spatialPanelOpen ? "<<" : ">>"}</span>
      <span>To Spatial Assignment</span>
    </button>
  </div>
)}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: theme.spacing.sm,
              alignItems: "center",
              background: theme.colors.background.Card,
              border: `1px solid ${theme.colors.borders.default}`,
              borderRadius: theme.borders.radius.lg,
              padding: theme.spacing.sm,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fontSizes.xs,
                  color: theme.colors.text.small,
                }}
              >
                {selectedItem.summaryLabel}
              </div>

              <div
                style={{
                  fontFamily: theme.fonts.heading,
                  fontSize: theme.fontSizes.lg,
                  fontWeight: theme.fontWeights.semiBold,
                  color: theme.colors.text.default,
                  lineHeight: 1.1,
                }}
              >
                {selectedItem.summaryValue}{" "}
                <span
                  style={{
                    fontSize: theme.fontSizes.sm,
                    fontWeight: theme.fontWeights.normal,
                    color: theme.colors.text.small,
                  }}
                >
                  {selectedItem.unit}
                </span>
              </div>
            </div>

            <div
              style={{
                padding: "4px 8px",
                borderRadius: theme.borders.radius.full,
                background: meta.lightBg,
                border: `1px solid ${meta.lightBorder}`,
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.xs,
                fontWeight: theme.fontWeights.medium,
                color: meta.color,
                whiteSpace: "nowrap",
              }}
            >
              {meta.label}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: theme.spacing.sm,
              flexWrap: "wrap",
            }}
          >
            <ActionTextButton
              label="Dupliquer"
              onClick={() => onDuplicateItem && onDuplicateItem(selectedItem.id)}
              disabled={readOnlyMode || disabled || !onDuplicateItem}
              variant="neutral"
              icon={<DuplicateIcon color={theme.colors.text.small} />}
            />

            <ActionTextButton
              label="Effacer"
              onClick={() => onDeleteItem && onDeleteItem(selectedItem.id)}
              disabled={
                readOnlyMode ||
                disabled ||
                !onDeleteItem ||
                !deleteState.canDelete
              }
              variant="danger"
              icon={<DeleteIcon color={theme.colors.text.danger} />}
              title={deleteState.reason}
            />
          </div>

          <div style={detailGridStyle}>
            {quickFields.map((field) => (
              <FieldRenderer
                key={field.name}
                field={field}
                value={selectedItem.fields?.[field.name]}
                onChange={(value) => updateField(selectedItem.id, field.name, value)}
                readOnlyMode={readOnlyMode}
                disabled={disabled}
              />
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing.sm,
              paddingTop: theme.spacing.sm,
              borderTop: `1px dashed ${theme.colors.borders.default}`,
            }}
          >
            <div
              style={{
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.xs,
                fontWeight: theme.fontWeights.medium,
                color: theme.colors.text.small,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              Paramétrage complémentaire
            </div>

            {detailFields.map((field) => (
              <FieldRenderer
                key={field.name}
                field={field}
                value={selectedItem.fields?.[field.name]}
                onChange={(value) => updateField(selectedItem.id, field.name, value)}
                readOnlyMode={readOnlyMode}
                disabled={disabled}
              />
            ))}
          </div>
        </div>

        {/* ==============================================================
           COLONNE DROITE — SPATIAL
        ============================================================== */}
        {isSpatialOpen && (
          <div
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing.sm,
              opacity: 1,
              transform: "translateX(0)",
              transition: "opacity 0.14s ease, transform 0.14s ease",
            }}
          >
            <AssetSpatialAssignmentPanel
              item={selectedItem}
              spatialEntities={spatialEntities}
              onAddSpatialAssignment={onAddSpatialAssignment}
              onRemoveSpatialAssignment={onRemoveSpatialAssignment}
              readOnlyMode={readOnlyMode}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const buildFallbackMeta = (family) => ({
  label: family,
  shortLabel: family,
  color: theme.colors.text.primary,
  lightBg: theme.colors.background.Card,
  lightBorder: theme.colors.borders.default,
});

const FieldRenderer = ({
  field,
  value,
  onChange,
  readOnlyMode = false,
  disabled = false,
}) => {
  if (!field) return null;

  switch (field.control) {
    case "number":
    case "text":
      return (
        <TsProperties
          label={field.label}
          labelWidth={field.labelWidth ?? 64}
          type={field.control === "number" ? "number" : "text"}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder || ""}
          disabled={readOnlyMode || disabled}
        />
      );

    case "select":
      return (
        <SelectField
          label={field.label}
          labelWidth={field.labelWidth ?? 64}
          value={value}
          options={field.options || []}
          onChange={onChange}
          disabled={readOnlyMode || disabled}
        />
      );

    case "stepper":
      return (
        <MiniStepper
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={readOnlyMode || disabled}
        />
      );

    default:
      return null;
  }
};

const ActionTextButton = ({
  label,
  onClick,
  disabled = false,
  variant = "neutral",
  icon = null,
  title = "",
}) => {
  const isDanger = variant === "danger";

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 28,
        padding: "0 10px",
        borderRadius: theme.borders.radius.full,
        border: `1px solid ${
          disabled
            ? theme.colors.borders.default
            : isDanger
            ? "#e5c5c5"
            : theme.colors.borders.default
        }`,
        background: disabled
          ? theme.colors.background.input
          : isDanger
          ? "#fff7f7"
          : theme.colors.background.surface,
        color: disabled
          ? theme.colors.text.verysmall
          : isDanger
          ? theme.colors.text.danger
          : theme.colors.text.primary,
        fontFamily: theme.fonts.body,
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        cursor: disabled ? "default" : "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </button>
  );
};

const DuplicateIcon = ({ color = "#5C7A89" }) => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="10" height="10" rx="2" />
      <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
    </svg>
  );
};

const DeleteIcon = ({ color = "#ef4444" }) => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 7h16" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
    </svg>
  );
};

const SelectField = ({
  label,
  labelWidth = 50,
  value,
  options = [],
  onChange,
  disabled = false,
}) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
      }}
    >
      {label && (
        <label
          style={{
            width: labelWidth,
            minWidth: labelWidth,
            fontFamily: theme.form.label.fontFamily,
            fontSize: theme.form.label.fontSize,
            fontWeight: theme.form.label.fontWeight,
            color: theme.form.label.color,
            lineHeight: 1.2,
          }}
        >
          {label}
        </label>
      )}

      <select
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.value)}
        style={{
          ...selectBaseStyle,
          color: disabled ? theme.colors.text.body : theme.form.field.color,
          background: disabled
            ? "transparent"
            : theme.colors.background.input,
          border: disabled ? "none" : selectBaseStyle.border,
          borderRadius: disabled ? 0 : theme.borders.radius.md,
          flex: 1,
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const MiniStepper = ({ label, value = 0, onChange, disabled = false }) => {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;

  const buttonStyle = {
    width: 24,
    height: 24,
    borderRadius: theme.borders.radius.md,
    border: `1px solid ${theme.colors.borders.default}`,
    background: theme.colors.background.input,
    color: theme.colors.text.primary,
    cursor: disabled ? "default" : "pointer",
    lineHeight: 1,
    padding: 0,
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
      }}
    >
      <label
        style={{
          width: 64,
          minWidth: 64,
          fontFamily: theme.form.label.fontFamily,
          fontSize: theme.form.label.fontSize,
          fontWeight: theme.form.label.fontWeight,
          color: theme.form.label.color,
          lineHeight: 1.2,
        }}
      >
        {label}
      </label>

      <div
        style={{
          flex: 1,
          height: 28,
          minHeight: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          padding: "2px 6px",
          border: `1px solid ${theme.colors.borders.default}`,
          borderRadius: theme.borders.radius.md,
          background: disabled ? "transparent" : theme.colors.background.input,
          boxSizing: "border-box",
        }}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange && onChange(Math.max(0, safeValue - 1))}
          style={buttonStyle}
        >
          −
        </button>

        <div
          style={{
            minWidth: 20,
            textAlign: "center",
            fontFamily: theme.form.field.fontFamily,
            fontSize: theme.form.field.fontSize,
            fontWeight: theme.fontWeights.medium,
            color: theme.form.field.color,
          }}
        >
          {safeValue}
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange && onChange(safeValue + 1)}
          style={buttonStyle}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default AssetWorkbenchDetailsPanel;